import React, { useEffect, useMemo, useState } from "react";
import SearchBar from "@/ui-components/search-bar";
import EmptyPlaceholder from "@/ui-components/empty-placeholder";
import {
  VirtuosoWrapper,
  GroupingUtils,
} from "@/ui-components/grouped-virtuoso";
import { trackSearchSubmit, trackSearchResults } from "@/utils/gtm";
import { getDisplayTime } from "@/shared/api/firehose/models";
import type { NewsFeedItem } from "@/shared/api/firehose/models";
import { useWidgetSearchState } from "@/hooks/useWidgetSearchState";
import {
  buildContentHubTextFilter,
  type ContentHubItem,
  type ContentHubSource,
} from "@/features/content-hub";
import { CommonSearchProps } from "@/widget-components/newsquawk-headlines/HeadlinesSearchForm";
import { useContentHubFirehoseSearch } from "./hooks/useContentHubFirehoseSearch";
import { FirehoseHighlightItem } from "../firehose-all/components";
import { DateSectionHeader } from "./DateSectionHeader";

export interface ContentHubFirehoseSearchProps extends CommonSearchProps {
  /** The widget's content-hub backend_source(s) — the only per-widget input the
   *  shared search needs (single-source widgets pass one, combined views a group). */
  sources: ContentHubSource | readonly ContentHubSource[];
  defaultTitle?: string;
  /** Per-widget mapper to the legacy story model (truth-social passes its own;
   *  the rest use the default source-dispatched mapper). */
  mapItem?: (item: ContentHubItem) => NewsFeedItem;
}

/**
 * Header text-search for the content-hub-backed firehose widgets (fin-x,
 * stocks-news, truth-social). Compiles the header query + the widget's
 * `backend_source`(s) into a `ContentHubFilter` (via `buildContentHubTextFilter`)
 * and runs it through the same `useContentHubFirehoseSearch` adapter the funnel
 * panel already uses — so the header box, the panel, and the base feed all share
 * one content-hub pipeline (cursor-paginated, live-updating).
 *
 * Results render exactly as before (`FirehoseHighlightItem` in a grouped
 * virtuoso overlay). Date filtering is intentionally omitted — content-hub
 * `/v1/search` has no date dimension.
 */
function ContentHubFirehoseSearchForm(props: ContentHubFirehoseSearchProps) {
  const {
    setTitle,
    formOpen = true,
    persistKey,
    onActiveChange,
    sources,
    mapItem,
    defaultTitle = "Firehose",
  } = props;

  const [savedSearch, setSavedSearch] = useWidgetSearchState(persistKey);
  const [query, setQuery] = useState<string>(savedSearch.query ?? "");
  // The committed query that actually drives the search (Enter / Search / a
  // persisted search re-applied on mount). Typing alone doesn't fetch.
  const [activeQuery, setActiveQuery] = useState<string>(savedSearch.query ?? "");

  const formRef = React.useRef<HTMLDivElement>(null);
  const [formHeight, setFormHeight] = useState<number>(40);

  const trimmed = activeQuery.trim();
  const filter = useMemo(
    () => buildContentHubTextFilter({ query: activeQuery, sources }),
    [activeQuery, sources]
  );
  const { stories, isLoading, isError, hasMore, loadNextPage, loadingNextPage } =
    useContentHubFirehoseSearch(filter, { enabled: !!trimmed, mapItem });

  const showSearchResults = !!trimmed;

  const handleSearch = (q: string) => {
    if (!q.trim()) return; // no date fallback — content-hub search is keyword-only
    setActiveQuery(q);
    setSavedSearch({ query: q });
    trackSearchSubmit({ search_term: q, search_context: "firehose" });
  };

  const handleClear = () => {
    setQuery("");
    setActiveQuery("");
    setSavedSearch({});
  };

  useEffect(() => {
    if (showSearchResults && stories.length > 0 && activeQuery) {
      trackSearchResults({
        search_term: activeQuery,
        search_context: "firehose",
        results_count: stories.length,
      });
    }
  }, [stories.length, activeQuery, showSearchResults]);

  useEffect(() => {
    if (showSearchResults) {
      setTitle?.(
        activeQuery
          ? `Showing results for '${activeQuery}'`
          : "Showing search results"
      );
    } else {
      setTitle?.(defaultTitle);
    }
    return () => setTitle?.(defaultTitle);
  }, [showSearchResults, activeQuery, setTitle, defaultTitle]);

  // Keep the container mounting this form while a search is in play (after Done),
  // and let it drop once the search is cleared.
  useEffect(() => {
    onActiveChange?.(showSearchResults);
  }, [showSearchResults, onActiveChange]);

  // Measure form height so the results overlay sits just below it.
  useEffect(() => {
    const update = () => {
      if (formRef.current) setFormHeight(formRef.current.offsetHeight);
    };
    update();
    const resizeObserver = new ResizeObserver(update);
    if (formRef.current) resizeObserver.observe(formRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <React.Fragment>
      <div
        className={`bg-gray-800 border-b border-gray-700/50 px-3 py-2 space-y-2${
          formOpen ? "" : " hidden"
        }`}
        ref={formRef}
      >
        <SearchBar
          value={query}
          onChange={setQuery}
          loading={isLoading}
          onSearch={handleSearch}
          onClear={handleClear}
        />

        <div className="sticky bottom-0 z-10 bg-gray-800 flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="ml-auto flex items-center gap-2">
            {(query || showSearchResults) && (
              <button
                type="button"
                onClick={handleClear}
                className="px-3 py-1 text-xs font-medium rounded text-gray-300 hover:bg-gray-700 transition-colors"
              >
                Clear
              </button>
            )}
            <button
              type="button"
              onClick={() => handleSearch(query)}
              disabled={!query.trim()}
              className="px-3 py-1 text-xs font-medium rounded bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Search
            </button>
            {props.onClose && (
              <button
                onClick={props.onClose}
                className="px-3 py-1 text-xs font-medium rounded bg-squawk-blue text-white hover:bg-squawk-blue/80 transition-colors"
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>

      {showSearchResults &&
        (stories.length > 0 ? (
          <section
            className="absolute flex-1 z-30 bg-white dark:bg-gray-900 inset-0 pb-[40px]"
            style={{ top: formOpen ? `${formHeight}px` : 0 }}
          >
            <VirtuosoWrapper
              data={stories}
              grouping={{
                groupBy: (items) =>
                  GroupingUtils.groupByDate(
                    items,
                    (item) => getDisplayTime(item),
                    { includeYesterday: true }
                  ),
                hideFirstGroupHeader: false,
              }}
              scrollManager={{ enabled: false }}
              pagination={{
                hasMore,
                isLoadingMore: loadingNextPage,
                loadNext: loadNextPage,
              }}
              renderItem={(item) => <FirehoseHighlightItem item={item} />}
              renderGroupHeader={(title) => <DateSectionHeader title={title} />}
              className="scrollbar-thin absolute inset-0"
            />
          </section>
        ) : !isLoading ? (
          <section
            className="absolute flex-1 z-30 bg-white dark:bg-gray-900 inset-0 pb-[40px]"
            style={{ top: formOpen ? `${formHeight}px` : 0 }}
          >
            <EmptyPlaceholder
              message={isError ? "Search failed. Please try again." : "No stories found"}
              isLoading={false}
            />
          </section>
        ) : null)}
    </React.Fragment>
  );
}

export default ContentHubFirehoseSearchForm;
